// Shared transport primitives for the per-BC httpPorts slice impls.
//
// One home for the pieces MORE THAN ONE slice needs: the `command` wrapper
// (the single impure boundary every command dispatch goes through) and the
// anchor→wire narrowing (used by both the Context slice and `sendTurn` in the
// Conversation slice). Slice-private helpers stay in their slice file.

import type * as Core from "@bilda/core";
import type { Result } from "@bilda/utils";
import type { ExternalError } from "../externalError.js";
import { postCommand } from "../httpClient.js";

// Every command goes through this wrapper so the impure boundary is applied
// in ONE place rather than remembered at each call site. `timeoutMs` is
// optional and forwarded to `postCommand`: omit it for the small-envelope
// default, pass the long turn ceiling for the multi-round turn commands.
// Each call is one intent and carries its own intent id: the server runs a
// command once per id and answers a repeat with the first result (#249).
export const command = (
    name: string,
    body: unknown,
    timeoutMs?: number,
): Promise<Result.Result<unknown, ExternalError>> =>
    postCommand(
        name,
        body,
        { "x-bilda-intent-id": globalThis.crypto.randomUUID() },
        timeoutMs,
    );

// A tab's anchor, on the wire, as `{kind; id}` — the same narrowing
// `appContract.introduceToContext`'s `refineAnchorRef` accepts server-side
// (context-model-explicit-ambient.md §7.6, tab-scoped doc §2.1: the anchor
// IS the key). Every other `EntityRef` variant (Concept/Term/…) has no tab
// surface today, so `null` here is a genuine "this caller passed something
// that cannot anchor a tab yet," not a transport failure — callers only
// ever construct resource anchors today (`AppShellHost`'s `activeContextAnchor`).
type AnchorWire = {
    readonly kind: "resource" | "conversation";
    readonly id: string;
};

export const anchorWire = (anchor: Core.EntityRef.Ref): AnchorWire | null => {
    switch (anchor.type) {
        case "whole_resource_ref":
        case "part_resource_ref":
            return { kind: "resource", id: anchor.id };
        case "conversation_ref":
            return { kind: "conversation", id: anchor.id };
        default:
            return null;
    }
};
